<template>
  <div class="phone">
    <div v-if="!composeEmailLoading">

      <!-- Primary header -->
      <header-vue
        ref="mainheader"
        :add-new="addNew"
        :hide-new="hideNew"
        :additional="additional"
        :isEditCicked="isEditCicked"
        v-on:loadUserEmails="loadUserEmails"
      />

      <transition name="swipe" mode="out-in">
        <!-- The mail folder list section -->
        <mailbox-section-vue
          v-if="this.$route.params.type == 'mailboxes'"
        />

        <!-- The mail list section -->
        <section-vue
          v-else
          ref="emailsection"
          :isEditCicked="isEditCicked"
          :selectAll="selectAll"
          :moveClicked="moveClicked"
          :moveToTrash="moveToTrash"
        />
      </transition>

      <!-- New Contact Alert Popup -->
      <new-contact-alert
        v-show="newContactAlert"
        :hideConfirmBox="hideConfirmBox"
      />

      <!-- The footer section -->
      <footer-vue
        :isEditCicked="isEditCicked"
      />
    </div>

    <!-- Loader -->
    <div class="loader-container" v-else>
      <loader-vue />
    </div>
  </div>
</template>

<script>
import HeaderVue from "./Header.vue";
import FooterVue from "./Footer.vue";
import NewContactAlert from './NewContactAlert.vue';
import LoaderVue from "../../Appointment/common/Loader.vue";
import SectionVue from "./Section.vue";
import MailboxSectionVue from "./MailboxSection.vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { authMixin } from "../../../mixins/authMixin";
import { permissionMixin } from "../../../mixins/permissionMixin";

export default {
  mixins: [authMixin, permissionMixin],
  data() {
    return {
      showList: "all",
      addNew: false,
      isEditCicked: false,
      selectAll: false,
      moveClicked: false,
      moveToTrash: false,
      addNewIcon: require("../../../assets/img/m_email-active.png"),
      newContactAlert: false
    };
  },
  components: {
    HeaderVue,
    SectionVue,
    MailboxSectionVue,
    LoaderVue,
    FooterVue,
    NewContactAlert
  },
  computed: {
    ...mapGetters("portalemail", {
      composeEmailLoading: "getComposeEmailLoading",
      emaillist: "getEmails",
      emailFolder: "getEmailFolder",
      boxCount: "getBoxCount"
    }),

    additional() {
      return {
        click: this.handleAddNewClick
      };
    }
  },
  watch: {
    async $route(to, from) {
      if(to !== from) {

        // Function to load folder list
        await this.loadPersonalFolder();

        // Fetching the inbox and unread folder count from server
        await this.fetchInboxUnreadCount(true);

        // Fetching the mail list if some one changes the route
        await this.loadRouteMail();
      }
    }
  },
  methods: {
    ...mapActions("portalemail", {
      loadFolderList: "loadFolderList",
      loadEmails: "loadEmails",
      fetchMailCount: "fetchMailCount"
    }),

    ...mapMutations("portalemail", {
      setComposeEmailLoading: "setComposeEmailLoading"
    }),

    async loadUserEmails() {
      // Load folder list
      await this.loadPersonalFolder();

      // Fetching the inbox and unread folder count from server
      await this.fetchInboxUnreadCount(true);

      // Fetching the mail list if some one refreshes the page
      await this.loadRouteMail();
    },

    /**
     * Function to load mail for a specific route
     */
    async loadRouteMail() {
      let type = this.$route.params.type;
      let folderId = this.$route.params.folderId;
      if('mailboxes' !== type) {

        // Get loadingStarted attribute
        let isFewPagesLoaded = false;
        if('custom' === type) {
          isFewPagesLoaded = this.emaillist[type][folderId] ? this.emaillist[type][folderId].isFewPagesLoaded : false;
        } else {
          isFewPagesLoaded = this.emaillist[type].isFewPagesLoaded;
        }

        // Check if loading has been started
        if(!isFewPagesLoaded) {
          await this.fetchMailList();
        }
      }
    },

    hideNew() {
      this.addNew = false;
    },

    /**
     * Function to hide the new email contact added alert box
     */
    hideConfirmBox() {
      this.newContactAlert = false;
    },

    /**
     * Function to clear the edit state.
     */
    clearEditState() {
      this.isEditCicked = false;
      this.selectAll = false;
      this.$refs.emailsection.selectedMailboxIDs = [];
    },

    /**
     * Function to fetch the mail list
     *
     * @returns {Promise<void>}
     */
    async fetchMailList() {
      let type = this.$route.params.type;

      if('mailboxes' === type) {
        return false;
      }

      // Get loadingFinished attribute
      let folderId = this.$route.params.folderId;
      let isAllPagesLoaded = false;
      if('custom' === type) {
        if(this.emaillist[type][folderId]) {
          isAllPagesLoaded = this.emaillist[type][folderId].isAllPagesLoaded;
        } else {
          isAllPagesLoaded = false;
        }
      } else {
        isAllPagesLoaded = this.emaillist[type].isAllPagesLoaded;
      }

      // Check if loading finished
      if(!isAllPagesLoaded) {
        await this.loadEmails({
          type: type,
          folderId: folderId ? folderId : 0
        });
      }
    },

    /**
     * Function to fetch number of emails on inbox/unread folder
     *
     * @returns {Promise<void>}
     */
    async fetchInboxUnreadCount(showLoader = true) {
      if(this.boxCount.needLoad) {
        // Show loader
        if (showLoader) {
          this.setComposeEmailLoading(true);
        }

        // Get the mail count from server
        let mailCount = await this.fetchMailCount();

        // Handling error
        if (mailCount.error) {
          if ((403 === mailCount.code) && ('/login' === this.$store.getters.getPreviousRoute)) {
            this.$router.push("/daily");
          } else {
            this.$store.dispatch("globalMessagePopup", mailCount.error);
          }
        }

        // Hide loader
        if (showLoader) {
          this.setComposeEmailLoading(false);
        }
      }
    },

    /**
     * Loading the emails with pagination
     */
    scroll() {
      /*window.onscroll = () => {*/
        let invoicesScrollPct = 0.5;
        // The max is written for cross browser functionality.
        // In Chrome/Safari/Opera if there’s no scroll, then documentElement.scrollHeight may be even less than
        // documentElement.clientHeight
        let windowScrollHeight = Math.max(
          document.body.scrollHeight, document.documentElement.scrollHeight,
          document.body.offsetHeight, document.documentElement.offsetHeight,
          document.body.clientHeight, document.documentElement.clientHeight
        );
        if ((window.innerHeight + window.pageYOffset) >= ((windowScrollHeight)*invoicesScrollPct)) {

          let type = this.$route.params.type;
          if('mailboxes' !== type) {

            // Get server request
            let isLoading = false;
            let folderId = this.$route.params.folderId;
            if('custom' === type) {
              if(this.emaillist[type][folderId]) {
                isLoading = this.emaillist[type][folderId].loading;
              } else {
                isLoading = false;
              }
            } else {
              isLoading = this.emaillist[type].loading;
            }

            // Check if server need to call
            if(false === isLoading) {
              this.fetchMailList();
            }
          }
        }
      /*};*/
    },

    /**
     * Function o load folder list
     **/
    async loadPersonalFolder() {
      if(!this.emailFolder) {

        // Get previous route
        let previousRoute = this.$store.getters.getPreviousRoute;

        // Show loader
        this.setComposeEmailLoading(true);

        // Get the folder list
        let folderList = await this.loadFolderList();

        // Handling error
        if (folderList.error) {
          if ((403 === folderList.code) && ('/login' === previousRoute)) {
            this.$router.push("/daily");
          } else {
            this.$store.dispatch("globalMessagePopup", folderList.error);
          }
        }

        // If there is any pending invitation, show the alert
        if (Number(folderList.pendingInvitation) > 0) {
          this.newContactAlert = true;
        }

        // Hide loader
        this.setComposeEmailLoading(false);
      }
    }
  },

  async created() {
    // Load folder list
    await this.loadPersonalFolder();

    // Fetching the inbox and unread folder count from server
    await this.fetchInboxUnreadCount(true);

    // Fetching the mail list if some one refreshes the page
    await this.loadRouteMail();
  },

  /**
   * Upon document mounted handle the scroll event listener
   */
  mounted() {
    window.addEventListener('scroll', this.scroll);
  },

  destroyed() {
    window.removeEventListener('scroll', this.scroll);
  }
};
</script>

<style scoped>
section {
  margin-top: 126px;
}

.add-new-icon {
  width: 35px;
  height: 35px;
}

.heading-end {
  cursor: pointer;
}

span.fill {
  flex-grow: 1;
}

.heading .heading-end {
  position: fixed;
  top: 54px;
  right: 14px;
  z-index: 9999;
}

.swipe-enter-active {
  animation: swipeIn 0.2s ease-in-out;
}

.swipe-leave-active {
  animation: swipeOut 0.2s ease-in-out;
}

.swipe-move {
  transition: all 0.2s ease-in-out;
}

@keyframes swipeIn {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }

  50% {
    opacity: 0.5;
    transform: translateX(-50px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes swipeOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }

  50% {
    opacity: 0.5;
    transform: translateX(-50px);
  }

  to {
    opacity: 0;
    transform: translateX(-100px);
  }
}
</style>
